import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Search, Loader2, Bot, Send, Check, X, RefreshCw, Zap, Shield, AlertTriangle,
  Target, ListChecks, Wrench, ChevronDown,
} from "lucide-react";

const CATEGORY_LABELS = {
  content: "Tartalom",
  local: "Helyi SEO",
  listings: "Hirdetések",
  technical: "Technikai",
  structured: "Structured data",
};

export function AdminSeoAi() {
  const [settings, setSettings] = useState(null);
  const [report, setReport] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [filterPriority, setFilterPriority] = useState("all");
  const [keywordsDraft, setKeywordsDraft] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sett, rep, sug] = await Promise.all([
        api.get("/admin/seo-ai/settings"),
        api.get("/admin/seo-ai/report/latest"),
        api.get("/admin/seo-ai/suggestions", { params: { status: "pending" } }),
      ]);
      setSettings(sett.data);
      const kws = sett.data?.target_keywords;
      setKeywordsDraft(Array.isArray(kws) ? kws.join(", ") : (kws || ""));
      setReport(rep.data?.score != null ? rep.data : null);
      setSuggestions(sug.data.suggestions || []);
    } catch (_) {
      toast.error("SEO AI betöltése sikertelen");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    try {
      const r = await api.patch("/admin/seo-ai/settings", patch);
      setSettings(r.data);
      toast.success("Beállítás mentve");
    } catch (_) {
      toast.error("Mentés sikertelen");
    }
  };

  const saveKeywords = () => {
    const list = keywordsDraft.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean).slice(0, 12);
    saveSettings({ target_keywords: list });
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.post("/admin/seo-ai/scan", null, { timeout: 180000 });
      const rep = r.data.report || {};
      setReport(rep);
      setSettings((s) => (s ? { ...s, last_score: rep.score, last_scan_at: new Date().toISOString() } : s));
      const sug = await api.get("/admin/seo-ai/suggestions", { params: { status: "pending" } });
      setSuggestions(sug.data.suggestions || []);
      const n = r.data.suggestions_created ?? 0;
      toast.success(
        `Scan kész: ${rep.score ?? "—"}/100 · ${rep.issue_count ?? 0} probléma · ${n} új javaslat (${rep.inventory_stats?.thin_descriptions ?? 0} rövid leírás)`,
      );
    } catch (e) {
      const msg = e.code === "ECONNABORTED"
        ? "Scan túl sokáig tartott (LLM ~1–2 perc) — próbáld újra."
        : (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Scan sikertelen");
      toast.error(msg);
    } finally {
      setScanning(false);
    }
  };

  const applyBatch = async (priority = "high") => {
    setBatchLoading(true);
    try {
      const r = await api.post("/admin/seo-ai/suggestions/apply-batch", { priority, limit: 25 });
      toast.success(`${r.data.applied} javaslat alkalmazva`);
      if (r.data.failed?.length) toast.warning(`${r.data.failed.length} sikertelen`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk sikertelen");
    } finally {
      setBatchLoading(false);
    }
  };

  const backfillSlugs = async () => {
    try {
      const r = await api.post("/admin/seo-ai/maintenance/backfill-slugs");
      toast.success(`Slug backfill kész — hátralévő: ${r.data.remaining_without_slug}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backfill sikertelen");
    }
  };

  const apply = async (id) => {
    try {
      await api.post(`/admin/seo-ai/suggestions/${id}/apply`);
      toast.success("Alkalmazva — a nyilvános oldalon frissül");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sikertelen");
    }
  };

  const reject = async (id) => {
    try {
      await api.post(`/admin/seo-ai/suggestions/${id}/reject`);
      load();
    } catch (_) {
      toast.error("Elutasítás sikertelen");
    }
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatLoading(true);
    const userMsg = { role: "user", content: text };
    const historyForApi = [...chatHistory.slice(-11), userMsg];
    setChatHistory((h) => [...h, userMsg]);
    setChatInput("");
    try {
      const r = await api.post("/admin/seo-ai/chat", { message: text, history: historyForApi }, { timeout: 120000 });
      const replyText = typeof r.data?.reply === "string" ? r.data.reply.trim() : "";
      setChatHistory((h) => [
        ...h,
        {
          role: "assistant",
          content: replyText || "Üres válasz érkezett — próbáld újra, vagy ellenőrizd az EMERGENT_LLM_KEY-et a szerveren.",
        },
      ]);
      if (r.data.suggestions?.length) {
        toast.info(`${r.data.suggestions.length} új javaslat a sorban`);
        load();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Chat hiba — várj 30–60 mp-et, majd próbáld újra.");
    } finally {
      setChatLoading(false);
    }
  };

  const score = report?.score ?? settings?.last_score;
  const scoreColor = score >= 80 ? "text-emerald-400" : score >= 55 ? "text-amber-400" : "text-red-400";
  const breakdown = report?.score_breakdown?.categories;
  const stats = report?.inventory_stats;
  const playbook = report?.playbook || [];

  const filteredSuggestions = useMemo(() => {
    if (filterPriority === "all") return suggestions;
    return suggestions.filter((s) => s.priority === filterPriority);
  }, [suggestions, filterPriority]);

  const sortedSuggestions = useMemo(
    () => [...filteredSuggestions].sort((a, b) => {
      const p = { high: 0, medium: 1, low: 2 };
      return (p[a.priority] ?? 2) - (p[b.priority] ?? 2);
    }),
    [filteredSuggestions],
  );

  if (loading && !settings) {
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-electric animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-seo-ai">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl font-light flex items-center gap-2">
            <Search className="w-6 h-6 text-electric" strokeWidth={1.5} />
            Auto SEO AI — audit + akcióterv
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">
            Többrétegű scan: tartalom, helyi jel, hirdetés minőség, technikai checklist és szabálymotor (azonnal alkalmazható javítások)
            + Gemini stratégia. A helyezés nem garantálható — a rendszer a legnagyobb organikus korlátokra fókuszál (meta, leírások, hely, slug).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={runScan} disabled={scanning} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric text-white text-sm disabled:opacity-50">
            {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {scanning ? "Mély scan…" : "Mély scan"}
          </button>
          <button type="button" disabled={batchLoading || !suggestions.length} onClick={() => applyBatch("high")} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/40 text-emerald-300 text-sm disabled:opacity-40">
            {batchLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            High priority bulk
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-5 lg:col-span-1" data-testid="seo-score-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Összesített SEO</div>
          <div className={`font-heading text-4xl font-light mt-2 ${scoreColor}`}>
            {score != null ? `${score}/100` : "—"}
          </div>
          {report?.issue_count != null && (
            <div className="text-xs text-slate-400 mt-2">{report.issue_count} audit pont · {report.car_issues} autó szintű</div>
          )}
          {stats && (
            <div className="mt-3 text-[11px] text-slate-500 space-y-1 border-t border-white/5 pt-2">
              <div>Készlet: {stats.total} autó</div>
              <div className="text-amber-400/90">Rövid leírás: {stats.thin_descriptions}</div>
              <div>Slug hiány: {stats.missing_slug} · Hely hiány: {stats.missing_location}</div>
              <div>SEO meta hiány: {stats.missing_seo_desc}</div>
            </div>
          )}
        </div>

        <div className="glass rounded-2xl p-5 lg:col-span-2">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <Target className="w-4 h-4 text-electric" /> Kategória bontás
          </div>
          {breakdown ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {Object.entries(breakdown).map(([cat, val]) => (
                <div key={cat} className="rounded-xl bg-white/[0.03] p-3 border border-white/5">
                  <div className="text-[10px] uppercase text-slate-500">{CATEGORY_LABELS[cat] || cat}</div>
                  <div className="text-lg font-medium mt-1">{val.score}/100</div>
                  <div className="text-[10px] text-slate-500">{val.issues} probléma</div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">Futtass mély scant a kategória pontokhoz.</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="text-sm font-medium mb-2 flex items-center gap-2">
            <Target className="w-4 h-4 text-electric" /> Cél kulcsszavak
          </div>
          <textarea
            value={keywordsDraft}
            onChange={(e) => setKeywordsDraft(e.target.value)}
            rows={2}
            className="w-full text-sm rounded-xl bg-black/30 border border-white/10 p-3"
            placeholder="használtautó, eladó autó Budapest, …"
            data-testid="seo-keywords"
          />
          <button type="button" onClick={saveKeywords} className="mt-2 text-xs px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15">
            Kulcsszavak mentése
          </button>
        </div>

        <div className="glass rounded-2xl p-5">
          <div className="text-sm font-medium mb-3 flex items-center gap-2">
            <Zap className="w-4 h-4 text-electric" /> Autopilot
          </div>
          <label className="flex items-center gap-3 text-sm text-slate-300 mb-3">
            <input type="checkbox" checked={!!settings?.autopilot_enabled} onChange={(e) => saveSettings({ autopilot_enabled: e.target.checked })} />
            Háttér scan (cron 6 óránként)
          </label>
          <div className="flex flex-wrap gap-2">
            {[
              { id: "off", label: "Csak javaslat" },
              { id: "safe", label: "Safe auto", desc: "seo.* + rule high" },
              { id: "aggressive", label: "Agresszív", desc: "+ autó rule" },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => saveSettings({ auto_apply_mode: m.id })}
                className={`text-left px-3 py-2 rounded-xl border text-xs ${
                  (settings?.auto_apply_mode || "off") === m.id ? "border-electric bg-electric/15" : "border-white/10 text-slate-400"
                }`}
              >
                <div className="font-medium">{m.label}</div>
                {m.desc && <div className="opacity-70">{m.desc}</div>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {playbook.length > 0 && (
        <div className="glass rounded-2xl p-5">
          <h3 className="font-medium flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-electric" /> Akcióterv (scan + manuális)
          </h3>
          <ul className="space-y-2">
            {playbook.map((step) => (
              <li key={step.id} className="flex gap-3 text-sm border-b border-white/5 pb-2 last:border-0">
                <span className={`text-[10px] uppercase shrink-0 pt-0.5 ${step.priority === "high" ? "text-red-400" : "text-amber-400"}`}>
                  {step.priority}
                </span>
                <div>
                  <div className="font-medium text-slate-200">{step.title}</div>
                  <div className="text-slate-400 text-xs mt-0.5">{step.detail}</div>
                </div>
                {step.id === "backfill_slugs" && (
                  <button type="button" onClick={backfillSlugs} className="ml-auto shrink-0 text-xs px-2 py-1 rounded-lg bg-white/10">
                    <Wrench className="w-3 h-3 inline" /> Slug fix
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-medium flex items-center gap-2">
              <Shield className="w-4 h-4 text-electric" /> Javaslatok ({sortedSuggestions.length})
            </h3>
            <div className="flex gap-1 text-xs">
              {["all", "high", "medium", "low"].map((p) => (
                <button key={p} type="button" onClick={() => setFilterPriority(p)} className={`px-2 py-1 rounded-full ${filterPriority === p ? "bg-electric/20 text-white" : "text-slate-500"}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>
          {sortedSuggestions.length === 0 ? (
            <p className="text-sm text-slate-500">Nincs pending — mély scan vagy chat.</p>
          ) : (
            sortedSuggestions.map((s) => (
              <div key={s.id} className="glass rounded-xl p-4 text-sm" data-testid={`seo-suggestion-${s.id}`}>
                <div className="flex flex-wrap justify-between gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-wider text-slate-500">
                    {s.priority} · {CATEGORY_LABELS[s.category] || s.category} · {s.scope}/{s.path}
                    {s.car_id ? ` · ${s.car_id.slice(0, 8)}` : ""}
                  </span>
                  <span className="text-[10px] text-amber-400/80">{s.source}</span>
                </div>
                <p className="text-slate-400 text-xs mb-2">{s.reason}</p>
                <p className="text-slate-200 whitespace-pre-wrap line-clamp-4">{s.proposed_value}</p>
                <div className="flex gap-2 mt-3">
                  <button type="button" onClick={() => apply(s.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs">
                    <Check className="w-3 h-3" /> Alkalmaz
                  </button>
                  <button type="button" onClick={() => reject(s.id)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/5 text-slate-400 text-xs">
                    <X className="w-3 h-3" /> Elutasít
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="space-y-4">
          {(report?.issues_sample?.length > 0) && (
            <details className="glass rounded-2xl p-5 group" open>
              <summary className="font-medium cursor-pointer flex items-center gap-2 list-none">
                <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                Audit részletek ({Math.min(20, report.issues_sample.length)} / {report.issue_count})
              </summary>
              <ul className="mt-3 max-h-52 overflow-y-auto text-[11px] text-slate-400 space-y-1">
                {report.issues_sample.slice(0, 20).map((iss, i) => (
                  <li key={`${iss.code}-${i}`}>
                    <span className={iss.severity === "high" ? "text-red-400" : iss.severity === "medium" ? "text-amber-400" : "text-slate-500"}>
                      [{iss.category}]
                    </span>{" "}
                    {iss.message}
                  </li>
                ))}
              </ul>
            </details>
          )}

          <div className="glass rounded-2xl p-5 flex flex-col min-h-[320px]" data-testid="seo-ai-chat">
            <h3 className="font-medium flex items-center gap-2 mb-3">
              <Bot className="w-4 h-4 text-electric" /> SEO stratégia chat
            </h3>
            <div className="flex-1 overflow-y-auto space-y-2 mb-3 max-h-64 text-sm">
              {chatHistory.length === 0 && (
                <p className="text-xs text-slate-500 flex gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  Pl.: „Készíts 90 napos organikus tervet Budapest + használtautó long-tailre.”
                </p>
              )}
              {chatHistory.map((m, i) => (
                <div key={i} className={`rounded-xl px-3 py-2 whitespace-pre-wrap ${m.role === "user" ? "bg-electric/20 ml-6" : "bg-white/[0.04] mr-6 text-slate-300"}`}>
                  {m.content}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendChat()}
                placeholder="Stratégia / konkrét mező…"
                className="flex-1 rounded-full bg-black/30 border border-white/10 px-4 py-2 text-sm"
              />
              <button type="button" disabled={chatLoading} onClick={sendChat} className="w-10 h-10 rounded-full bg-electric flex items-center justify-center disabled:opacity-50">
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
